import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Eye, EyeOff,
  Home, LayoutGrid, Loader2, Plus, RefreshCw, Search, Settings,
  Users, BarChart3, List, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  APPOINTMENT_STATUS,
  addDays, buildDateTime, formatDisplayDate, formatShortDate, formatWeekdayShort,
  getViewRange, minutesToTime, snapMinutes, startOfMonth, startOfWeek, timeToMinutes, toYmd, daysInMonth,
} from "@/utils/calendarHelpers";
import {
  CalendarEventCard, DoctorMultiSelect, GRID_DAY_END, GRID_DAY_START, GRID_HOUR_HEIGHT,
  buildGridStyle, filterDoctorsBySelection, getGridEventStyle, getPatientName, matchesDoctorId,
} from "@/components/calendar/calendarShared";
import "./appointment-calendar.css";

const VIEW_MODES = [
  { id: "day", label: "Gün" },
  { id: "week", label: "Hafta" },
  { id: "month", label: "Ay" },
];

const SIDEBAR_ITEMS = [
  { id: "home", label: "Anasayfa", icon: Home },
  { id: "calendar", label: "Takvim", icon: CalendarIcon },
  { id: "patients", label: "Hastalar", icon: Users },
  { id: "appointments", label: "Randevular", icon: List },
  { id: "reports", label: "Raporlar", icon: BarChart3 },
  { id: "settings", label: "Ayarlar", icon: Settings },
];

const AppointmentCalendarPage = ({ apiBase, isDark, textClass, labelClass, cardClass, inputClass }) => {
  const [section, setSection] = useState("calendar");
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState("day");
  const [doctorFilter, setDoctorFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [showEmpty, setShowEmpty] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [syncInfo, setSyncInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [detailAppt, setDetailAppt] = useState(null);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [dragApptId, setDragApptId] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    api_base_url: "https://backend.medicasimple.com/0.0.1",
    username: "",
    password: "",
    sync_interval_minutes: 10,
    sync_past_days: 7,
    sync_future_days: 30,
  });
  const [newForm, setNewForm] = useState({
    doctor_id: "",
    patient_name: "",
    date: toYmd(new Date()),
    start: "09:00",
    end: "09:30",
    status_key: "waiting",
    note: "",
  });

  const viewRange = useMemo(() => getViewRange(viewMode, selectedDate), [viewMode, selectedDate]);
  const hourCount = GRID_DAY_END - GRID_DAY_START;
  const hours = useMemo(() => Array.from({ length: hourCount }, (_, i) => GRID_DAY_START + i), [hourCount]);

  const visibleDoctors = useMemo(
    () => filterDoctorsBySelection(doctors, doctorFilter),
    [doctors, doctorFilter]
  );

  const filteredAppointments = useMemo(() => {
    const term = search.trim().toLowerCase();
    return appointments.filter((a) => {
      if (viewMode === "day" && a.date !== toYmd(selectedDate)) return false;
      if (!term) return true;
      const patient = getPatientName(a).toLowerCase();
      return patient.includes(term) || (a.note || "").toLowerCase().includes(term);
    });
  }, [appointments, search, viewMode, selectedDate]);

  const statusCounts = useMemo(() => {
    const counts = {};
    filteredAppointments.forEach((a) => {
      counts[a.status] = (counts[a.status] || 0) + 1;
    });
    return counts;
  }, [filteredAppointments]);

  const getEventStyle = (start, end) => getGridEventStyle(start, end, GRID_DAY_START, GRID_HOUR_HEIGHT);

  const loadData = useCallback(async (autoSync = true) => {
    if (!apiBase) return;
    setLoading(true);
    try {
      const params = {
        start_date: viewRange.startYmd,
        end_date: viewRange.endYmd,
        search,
      };
      if (doctorFilter.length) params.doctor_ids = doctorFilter.join(",");
      if (autoSync) params.auto_sync = "1";

      const [calRes, docRes] = await Promise.all([
        axios.get(`${apiBase}/calendar`, { params }),
        axios.get(`${apiBase}/calendar`, { params: { doctors: 1 } }),
      ]);
      setAppointments(calRes.data?.items || []);
      setSyncInfo(calRes.data?.sync || null);
      setDoctors(docRes.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Takvim verisi yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiBase, viewRange.startYmd, viewRange.endYmd, search, doctorFilter]);

  const loadPatients = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await axios.get(`${apiBase}/calendar`, { params: { patients: 1, search, limit: 200 } });
      setPatients(res.data?.items || []);
    } catch (_) { /* ignore */ }
  }, [apiBase, search]);

  const loadSettings = useCallback(async () => {
    if (!apiBase) return;
    try {
      const res = await axios.get(`${apiBase}/calendar`, { params: { settings: 1 } });
      setSyncInfo(res.data);
      setSettingsForm((f) => ({
        ...f,
        api_base_url: res.data.api_base_url || f.api_base_url,
        sync_interval_minutes: res.data.sync_interval_minutes ?? 10,
        sync_past_days: res.data.sync_past_days ?? 7,
        sync_future_days: res.data.sync_future_days ?? 30,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Takvim ayarları yüklenemedi");
    }
  }, [apiBase]);

  const runSync = useCallback(async (manual = true) => {
    if (!apiBase) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${apiBase}/calendar?sync=1`, { sync: true, trigger: manual ? "manual" : "auto" });
      toast.success(res.data?.message || "Senkron tamamlandı");
      await loadData(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senkron başarısız");
    } finally {
      setSyncing(false);
    }
  }, [apiBase, loadData]);

  useEffect(() => { loadData(true); }, [loadData]);
  useEffect(() => {
    if (section === "patients") loadPatients();
    if (section === "settings") loadSettings();
  }, [section, loadPatients, loadSettings]);

  useEffect(() => {
    if (!syncInfo?.has_credentials) return undefined;
    const intervalMs = Math.max(60000, (syncInfo.sync_interval_minutes || 10) * 60 * 1000);
    const t = setInterval(() => { runSync(false); }, intervalMs);
    return () => clearInterval(t);
  }, [runSync, syncInfo?.has_credentials, syncInfo?.sync_interval_minutes]);

  const navStep = viewMode === "week" ? 7 : viewMode === "month" ? 30 : 1;
  const mobileDoctors = visibleDoctors.length ? visibleDoctors : doctors.slice(0, 1);

  const handleDropOnColumn = async (doctorId, clientY, colEl) => {
    if (!dragApptId || !colEl) return;
    const appt = appointments.find((a) => a.id === dragApptId);
    if (!appt) return;
    const rect = colEl.getBoundingClientRect();
    const offsetY = clientY - rect.top;
    const minsFromStart = snapMinutes(DAY_START_HOUR * 60 + (offsetY / HOUR_HEIGHT) * 60);
    const duration = timeToMinutes(appt.end) - timeToMinutes(appt.start);
    const newStart = minutesToTime(Math.max(DAY_START_HOUR * 60, Math.min(DAY_END_HOUR * 60 - duration, minsFromStart)));
    const newEnd = minutesToTime(timeToMinutes(newStart) + duration);
    const targetDate = toYmd(selectedDate);

    try {
      await axios.put(`${apiBase}/calendar?id=${dragApptId}`, {
        doctor_id: doctorId,
        start_at: buildDateTime(selectedDate, newStart),
        end_at: buildDateTime(selectedDate, newEnd),
      });
      setAppointments((prev) => prev.map((a) => (
        a.id === dragApptId
          ? { ...a, doctorId, start: newStart, end: newEnd, date: targetDate, startAt: buildDateTime(selectedDate, newStart), endAt: buildDateTime(selectedDate, newEnd) }
          : a
      )));
      toast.success("Randevu taşındı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Taşıma başarısız");
    } finally {
      setDragApptId(null);
    }
  };

  const saveDetail = async () => {
    if (!detailAppt) return;
    try {
      await axios.put(`${apiBase}/calendar?id=${detailAppt.id}`, {
        status_key: detailAppt.status,
        status_name: APPOINTMENT_STATUS[detailAppt.status]?.label,
        note: detailAppt.note,
      });
      setAppointments((prev) => prev.map((a) => (a.id === detailAppt.id ? { ...a, ...detailAppt } : a)));
      toast.success("Güncellendi");
      setDetailAppt(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Güncelleme başarısız");
    }
  };

  const deleteAppt = async () => {
    if (!detailAppt) return;
    try {
      await axios.delete(`${apiBase}/calendar?id=${detailAppt.id}`);
      setAppointments((prev) => prev.filter((a) => a.id !== detailAppt.id));
      toast.success("Randevu silindi");
      setDetailAppt(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silme başarısız");
    }
  };

  const createAppt = async () => {
    try {
      await axios.post(`${apiBase}/calendar`, {
        doctor_id: Number(newForm.doctor_id),
        patient_name: newForm.patient_name,
        start_at: `${newForm.date} ${newForm.start}:00`,
        end_at: `${newForm.date} ${newForm.end}:00`,
        status_key: newForm.status_key,
        note: newForm.note,
      });
      toast.success("Randevu oluşturuldu");
      setNewModalOpen(false);
      loadData(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Oluşturma başarısız");
    }
  };

  const saveSettings = async (andSync = false) => {
    try {
      const payload = { settings: true, ...settingsForm };
      if (!payload.password) delete payload.password;
      const res = await axios.post(`${apiBase}/calendar`, payload);
      setSyncInfo(res.data);
      toast.success("Ayarlar kaydedildi");
      if (andSync) await runSync(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ayarlar kaydedilemedi");
    }
  };

  const renderDayGrid = (dayDate, cols) => {
    const dayYmd = toYmd(dayDate);
    const dayAppts = filteredAppointments.filter((a) => a.date === dayYmd);
    const gridStyle = { "--appt-doctor-count": cols.length, "--appt-hour-count": hourCount, "--appt-hour-h": `${GRID_HOUR_HEIGHT}px` };

    return (
      <div key={dayYmd} className={`rounded-xl border overflow-hidden ${cardClass}`}>
        {viewMode !== "day" && (
          <div className={`px-3 py-2 border-b text-xs font-semibold ${textClass}`}>
            {formatWeekdayShort(dayDate)} · {formatShortDate(dayDate)}
          </div>
        )}
        <div className="appt-cal-v2-grid border-b border-[var(--appt-grid-line)]">
          <div className={`p-4 text-[11px] font-semibold uppercase tracking-wider ${labelClass}`}>Saat</div>
          {cols.map((doc) => (
            <div key={doc.id} className={`p-4 border-l border-[var(--appt-grid-line)] appt-cal-v2-doc-head ${isDark ? "bg-slate-900/30" : "bg-gray-50/90"}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${doc.tone} text-white text-xs font-bold flex items-center justify-center shrink-0 shadow-sm`}>
                  {doc.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold truncate leading-tight ${textClass}`}>{doc.name}</p>
                  <p className={`text-xs truncate mt-0.5 ${labelClass}`}>{doc.specialty || "Doktor"}</p>
                </div>
                <span className={`text-xs tabular-nums px-2 py-0.5 rounded-md shrink-0 ${isDark ? "bg-slate-800 text-slate-300" : "bg-white text-gray-600 border border-gray-200 shadow-sm"}`}>
                  {dayAppts.filter((a) => matchesDoctorId(a.doctorId, doc.id)).length}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] appt-cal-v2-scroll">
          <div className="appt-cal-v2-grid relative" style={gridStyle}>
            <div className="appt-cal-time-col">
              {hours.map((h) => (
                <div key={h} className={`appt-cal-v2-hour-label ${labelClass}`} style={{ height: GRID_HOUR_HEIGHT }}>
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>
            {cols.map((doc) => (
              <div
                key={doc.id}
                className="appt-cal-v2-doctor-col"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDropOnColumn(doc.id, e.clientY, e.currentTarget)}
              >
                {dayAppts.filter((a) => matchesDoctorId(a.doctorId, doc.id)).map((appt) => (
                  <CalendarEventCard
                    key={appt.id}
                    item={appt}
                    isDark={isDark}
                    style={getEventStyle(appt.start, appt.end)}
                    draggable
                    onDragStart={() => setDragApptId(appt.id)}
                    onClick={() => setDetailAppt({ ...appt })}
                  />
                ))}
                {!showEmpty && dayAppts.filter((a) => matchesDoctorId(a.doctorId, doc.id)).length === 0 && (
                  <div className={`absolute inset-0 flex items-center justify-center text-[10px] ${labelClass} opacity-40`}>Boş</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const start = startOfWeek(selectedDate);
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    const cols = mobileDoctors.length <= 2 ? mobileDoctors : mobileDoctors.slice(0, 1);
    return (
      <div className="space-y-3">
        {days.map((d) => renderDayGrid(d, cols))}
      </div>
    );
  };

  const renderMonthView = () => {
    const first = startOfMonth(selectedDate);
    const total = daysInMonth(selectedDate);
    const startPad = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), d));

    return (
      <div className={`rounded-xl border p-3 ${cardClass}`}>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d) => (
            <div key={d} className={`text-center text-[10px] font-semibold ${labelClass}`}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`e-${idx}`} className="h-20" />;
            const ymd = toYmd(cell);
            const dayItems = filteredAppointments.filter((a) => a.date === ymd);
            const isToday = ymd === toYmd(new Date());
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => { setSelectedDate(cell); setViewMode("day"); }}
                className={`h-20 rounded-lg border p-1.5 text-left transition-colors ${isDark ? "border-slate-800 hover:bg-slate-800/50" : "border-gray-100 hover:bg-gray-50"} ${isToday ? "ring-2 ring-blue-500/50" : ""}`}
              >
                <div className={`text-xs font-semibold ${textClass}`}>{cell.getDate()}</div>
                <div className="mt-1 space-y-0.5">
                  {dayItems.slice(0, 3).map((a) => {
                    const st = APPOINTMENT_STATUS[a.status] || APPOINTMENT_STATUS.other;
                    return (
                      <div key={a.id} className="flex items-center gap-1 truncate text-[9px]">
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                        <span className="truncate">{a.start} {getPatientName(a)}</span>
                      </div>
                    );
                  })}
                  {dayItems.length > 3 && <div className={`text-[9px] ${labelClass}`}>+{dayItems.length - 3}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderHome = () => (
    <div className="grid gap-4 md:grid-cols-3">
      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <p className={`text-sm ${labelClass}`}>Toplam randevu</p>
        <p className={`text-3xl font-bold mt-1 ${textClass}`}>{filteredAppointments.length}</p>
      </div>
      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <p className={`text-sm ${labelClass}`}>Aktif doktor</p>
        <p className={`text-3xl font-bold mt-1 ${textClass}`}>{doctors.length}</p>
      </div>
      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <p className={`text-sm ${labelClass}`}>Son senkron</p>
        <p className={`text-sm font-medium mt-2 ${textClass}`}>{syncInfo?.last_sync_at || "Henüz yok"}</p>
        <p className={`text-xs mt-1 ${labelClass}`}>{syncInfo?.last_sync_message || ""}</p>
      </div>
      <div className={`md:col-span-3 rounded-xl border p-4 ${cardClass}`}>
        <p className={`text-sm font-semibold mb-3 ${textClass}`}>Durum dağılımı</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(APPOINTMENT_STATUS).map(([key, meta]) => (
            <span key={key} className={`inline-flex items-center gap-1.5 text-xs ${labelClass}`}>
              <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
              {meta.label}: {statusCounts[key] ?? 0}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderListSection = (title, rows, columns) => (
    <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
      <div className={`px-4 py-3 border-b font-semibold ${textClass}`}>{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className={isDark ? "bg-slate-900/50" : "bg-gray-50"}>
            <tr>{columns.map((c) => <th key={c.key} className={`px-3 py-2 text-left ${labelClass}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className={`px-3 py-6 text-center ${labelClass}`}>Kayıt yok</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                {columns.map((c) => <td key={c.key} className={`px-3 py-2 ${textClass}`}>{c.render ? c.render(row) : row[c.key]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className={`appt-cal-root appt-cal-shell appt-cal-v2-root ${isDark ? "dark" : ""}`}>
      <aside className={`appt-cal-sidebar hidden lg:flex flex-col ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"} border-r`}>
        <div className={`px-4 py-4 border-b ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-blue-500" />
            <span className={`font-bold ${textClass}`}>mediCrm</span>
          </div>
        </div>
        <nav className="flex-1 p-2 space-y-0.5">
          {SIDEBAR_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = section === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSection(item.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active
                    ? "bg-blue-600 text-white"
                    : isDark ? "text-slate-300 hover:bg-slate-800" : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="appt-cal-main flex-1 min-w-0">
        <div className={`appt-cal-sticky-header sticky top-0 z-20 border-b px-3 py-3 md:px-4 ${isDark ? "bg-slate-950/95 border-slate-800 backdrop-blur" : "bg-white/95 border-gray-200 backdrop-blur"}`}>
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={toYmd(selectedDate)} onChange={(e) => setSelectedDate(new Date(e.target.value + "T12:00:00"))} className={`h-9 w-36 text-xs ${inputClass}`} />
              <div className={`inline-flex items-center rounded-lg border p-0.5 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"}`}>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelectedDate((d) => addDays(d, -navStep))}><ChevronLeft className="w-4 h-4" /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-8 px-3 text-xs" onClick={() => setSelectedDate(new Date())}>Bugün</Button>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => setSelectedDate((d) => addDays(d, navStep))}><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <span className={`text-sm font-medium hidden md:inline ${textClass}`}>{formatDisplayDate(selectedDate)}</span>
              {section === "calendar" && (
                <div className={`inline-flex rounded-lg p-0.5 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                  {VIEW_MODES.map((mode) => (
                    <button key={mode.id} type="button" onClick={() => setViewMode(mode.id)} className={`px-3 py-1.5 text-xs rounded-md font-medium ${viewMode === mode.id ? "bg-blue-600 text-white" : isDark ? "text-slate-400" : "text-gray-600"}`}>{mode.label}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" className={`h-9 ${inputClass}`} disabled={syncing} onClick={() => runSync(true)}>
                {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Senkron
              </Button>
              <Button type="button" size="sm" className="h-9" onClick={() => setNewModalOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Yeni Randevu
              </Button>
            </div>
          </div>
        </div>

        <div className="p-3 md:p-4 space-y-3">
          <nav className="flex lg:hidden gap-1 overflow-x-auto pb-1">
            {SIDEBAR_ITEMS.map((item) => (
              <button key={item.id} type="button" onClick={() => setSection(item.id)} className={`shrink-0 px-3 py-1.5 rounded-full text-xs ${section === item.id ? "bg-blue-600 text-white" : isDark ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-gray-700"}`}>
                {item.label}
              </button>
            ))}
          </nav>

          {section === "calendar" && (
            <>
              <div className={`rounded-xl border p-3 flex flex-col lg:flex-row lg:items-center gap-3 ${cardClass}`}>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${labelClass}`} />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hasta adıyla ara..." className={`pl-9 h-9 ${inputClass}`} />
                </div>
                <DoctorMultiSelect doctors={doctors} values={doctorFilter} onChange={setDoctorFilter} isDark={isDark} inputClass={inputClass} labelClass={labelClass} />
                <Button type="button" variant="outline" size="sm" className={`h-9 shrink-0 ${inputClass}`} onClick={() => setShowEmpty((v) => !v)}>
                  {showEmpty ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  Boş saatleri {showEmpty ? "göster" : "gizle"}
                </Button>
                <div className={`flex flex-wrap items-center gap-2 text-xs ${labelClass}`}>
                  <span className="font-medium">Toplam: {filteredAppointments.length}</span>
                  {Object.entries(APPOINTMENT_STATUS).slice(0, 5).map(([key, meta]) => (
                    <span key={key} className="inline-flex items-center gap-1" title={meta.label}>
                      <span className={`w-2 h-2 rounded-full ${meta.dot}`} />{statusCounts[key] ?? 0}
                    </span>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className={`rounded-xl border p-12 text-center ${cardClass}`}>
                  <Loader2 className={`w-8 h-8 mx-auto animate-spin ${labelClass}`} />
                  <p className={`text-sm mt-3 ${labelClass}`}>Randevular yükleniyor...</p>
                </div>
              ) : doctors.length === 0 ? (
                <div className={`rounded-xl border p-8 text-center ${cardClass}`}>
                  <CalendarIcon className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  <p className={`text-sm font-medium ${textClass}`}>Henüz randevu verisi yok</p>
                  <p className={`text-xs mt-1 mb-4 ${labelClass}`}>Ayarlar bölümünden MedicaSimple bilgilerini girip senkron yapın.</p>
                  <Button type="button" size="sm" onClick={() => setSection("settings")}>Ayarlara git</Button>
                </div>
              ) : viewMode === "day" ? (
                renderDayGrid(selectedDate, visibleDoctors.length ? visibleDoctors : doctors)
              ) : viewMode === "week" ? (
                renderWeekView()
              ) : (
                renderMonthView()
              )}

              {syncInfo?.last_sync_at && (
                <p className={`text-[11px] text-center ${labelClass}`}>
                  Son senkron: {syncInfo.last_sync_at} · {syncInfo.last_sync_message || ""} · Otomatik: {syncInfo.sync_interval_minutes || 10} dk
                </p>
              )}
            </>
          )}

          {section === "home" && renderHome()}
          {section === "patients" && renderListSection("Hastalar", patients, [
            { key: "full_name", label: "Ad Soyad" },
            { key: "phone", label: "Telefon" },
          ])}
          {section === "appointments" && renderListSection("Randevular", filteredAppointments, [
            { key: "date", label: "Tarih" },
            { key: "start", label: "Saat", render: (r) => `${r.start}–${r.end}` },
            { key: "patient", label: "Hasta" },
            { key: "doctorName", label: "Doktor" },
            { key: "status", label: "Durum", render: (r) => APPOINTMENT_STATUS[r.status]?.label || r.statusName },
          ])}
          {section === "reports" && (
            <div className={`rounded-xl border p-8 text-center ${cardClass}`}>
              <BarChart3 className={`w-10 h-10 mx-auto mb-3 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
              <p className={`text-sm font-medium ${textClass}`}>Raporlar</p>
              <p className={`text-xs mt-1 ${labelClass}`}>Anasayfa durum istatistiklerini kullanın; detaylı raporlar yakında.</p>
            </div>
          )}
          {section === "settings" && (
            <div className={`rounded-xl border p-4 md:p-6 max-w-xl space-y-4 ${cardClass}`}>
              <h3 className={`font-semibold ${textClass}`}>MedicaSimple API Ayarları</h3>
              {syncInfo && (
                <div className={`rounded-lg border p-3 text-xs space-y-1 ${isDark ? "border-slate-700 bg-slate-900/50" : "border-gray-200 bg-gray-50"}`}>
                  <p className={labelClass}>
                    Kimlik bilgisi: {syncInfo.has_credentials ? <span className="text-emerald-500 font-medium">Kayıtlı</span> : <span className="text-amber-500 font-medium">Eksik — senkron çalışmaz</span>}
                  </p>
                  {syncInfo.last_sync_at && <p className={labelClass}>Son senkron: {syncInfo.last_sync_at}</p>}
                  {syncInfo.last_sync_status && (
                    <p className={syncInfo.last_sync_status === "ok" ? "text-emerald-500" : "text-rose-500"}>
                      Durum: {syncInfo.last_sync_status} — {syncInfo.last_sync_message || ""}
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-3">
                <div><Label className={labelClass}>API Base URL</Label><Input value={settingsForm.api_base_url} onChange={(e) => setSettingsForm((f) => ({ ...f, api_base_url: e.target.value }))} className={`mt-1 ${inputClass}`} /></div>
                <div><Label className={labelClass}>Kullanıcı adı (e-posta)</Label><Input value={settingsForm.username} onChange={(e) => setSettingsForm((f) => ({ ...f, username: e.target.value }))} placeholder="ornek@klinik.com" className={`mt-1 ${inputClass}`} /></div>
                <div><Label className={labelClass}>Şifre</Label><Input type="password" value={settingsForm.password} onChange={(e) => setSettingsForm((f) => ({ ...f, password: e.target.value }))} placeholder={syncInfo?.has_credentials ? "Değiştirmek için yazın" : "MedicaSimple şifresi"} className={`mt-1 ${inputClass}`} /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className={labelClass}>Senkron (dk)</Label><Input type="number" min={1} value={settingsForm.sync_interval_minutes} onChange={(e) => setSettingsForm((f) => ({ ...f, sync_interval_minutes: Number(e.target.value) }))} className={`mt-1 ${inputClass}`} /></div>
                  <div><Label className={labelClass}>Geçmiş (gün)</Label><Input type="number" min={0} value={settingsForm.sync_past_days} onChange={(e) => setSettingsForm((f) => ({ ...f, sync_past_days: Number(e.target.value) }))} className={`mt-1 ${inputClass}`} /></div>
                  <div><Label className={labelClass}>İleri (gün)</Label><Input type="number" min={1} value={settingsForm.sync_future_days} onChange={(e) => setSettingsForm((f) => ({ ...f, sync_future_days: Number(e.target.value) }))} className={`mt-1 ${inputClass}`} /></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => saveSettings(false)}>Kaydet</Button>
                <Button type="button" variant="secondary" disabled={syncing} onClick={() => saveSettings(true)}>
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Kaydet ve Senkron
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!detailAppt} onOpenChange={(o) => !o && setDetailAppt(null)}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : ""}>
          <DialogHeader><DialogTitle>Randevu Detayı</DialogTitle></DialogHeader>
          {detailAppt && (
            <div className="space-y-3 text-sm">
              <p><strong>Hasta:</strong> {detailAppt.patient}</p>
              <p><strong>Doktor:</strong> {detailAppt.doctorName}</p>
              <p><strong>Tarih/Saat:</strong> {detailAppt.date} {detailAppt.start}–{detailAppt.end}</p>
              <div>
                <Label>Durum</Label>
                <Select value={detailAppt.status} onValueChange={(v) => setDetailAppt((a) => ({ ...a, status: v }))}>
                  <SelectTrigger className={`mt-1 ${inputClass}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(APPOINTMENT_STATUS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Not</Label>
                <Input value={detailAppt.note || ""} onChange={(e) => setDetailAppt((a) => ({ ...a, note: e.target.value }))} className={`mt-1 ${inputClass}`} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="destructive" size="sm" onClick={deleteAppt}><Trash2 className="w-4 h-4 mr-1" /> Sil</Button>
            <Button type="button" size="sm" onClick={saveDetail}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newModalOpen} onOpenChange={setNewModalOpen}>
        <DialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : ""}>
          <DialogHeader><DialogTitle>Yeni Randevu</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Doktor</Label>
              <Select value={newForm.doctor_id} onValueChange={(v) => setNewForm((f) => ({ ...f, doctor_id: v }))}>
                <SelectTrigger className={`mt-1 ${inputClass}`}><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Hasta adı</Label><Input value={newForm.patient_name} onChange={(e) => setNewForm((f) => ({ ...f, patient_name: e.target.value }))} className={`mt-1 ${inputClass}`} /></div>
            <div><Label>Tarih</Label><Input type="date" value={newForm.date} onChange={(e) => setNewForm((f) => ({ ...f, date: e.target.value }))} className={`mt-1 ${inputClass}`} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Başlangıç</Label><Input type="time" value={newForm.start} onChange={(e) => setNewForm((f) => ({ ...f, start: e.target.value }))} className={`mt-1 ${inputClass}`} /></div>
              <div><Label>Bitiş</Label><Input type="time" value={newForm.end} onChange={(e) => setNewForm((f) => ({ ...f, end: e.target.value }))} className={`mt-1 ${inputClass}`} /></div>
            </div>
          </div>
          <DialogFooter><Button type="button" onClick={createAppt}>Oluştur</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentCalendarPage;
